import { type ComponentProps, useContext } from 'react';

import { DataContext, Tooltip } from '@visx/xychart';

import './cursor.css';
import { type FormatDataPointLabel, KeyboardNavigation } from './KeyboardNavigation';

/**
 * Adds interactive cursor functionality with tooltip and keyboard navigation.
 *
 * Displays a cursor that follows the mouse and shows an optional tooltip at the nearest
 * data point. Includes full keyboard navigation support for accessibility.
 *
 * @example
 * ```tsx
 * <XYChart width={500} height={300}>
 *   <AreaSeries data={data} dataKey="series1" xAccessor={d => d.x} yAccessor={d => d.y} />
 *   <Cursor
 *     formatDataPointLabel={(d, index, total) =>
 *       `Point ${index + 1} of ${total}: ${d.value}`
 *     }
 *     renderTooltip={({ tooltipData }) => (
 *       <div>Value: {tooltipData?.nearestDatum?.datum?.value}</div>
 *     )}
 *   />
 * </XYChart>
 * ```
 */
export const Cursor = <T extends object = object>({
  renderTooltip = () => '',
  formatDataPointLabel,
}: {
  renderTooltip?: ComponentProps<typeof Tooltip>['renderTooltip'];
  formatDataPointLabel: FormatDataPointLabel<T>;
}) => {
  const { theme } = useContext(DataContext) ?? {};
  const primaryColor = theme?.colors[0];

  return (
    <>
      <KeyboardNavigation<T> formatDataPointLabel={formatDataPointLabel} />
      <Tooltip
        snapTooltipToDatumX={true}
        snapTooltipToDatumY={true}
        showVerticalCrosshair={true}
        showSeriesGlyphs={true}
        renderGlyph={({ color }) => <circle r={4} fill={color} stroke="white" strokeWidth={2} />}
        verticalCrosshairStyle={{
          stroke: primaryColor,
          strokeWidth: 1,
        }}
        renderTooltip={renderTooltip}
      />
    </>
  );
};
